/**
 * 全プロンプト（ONP 35 + 顧客レポート 24）の集約カタログ。
 *
 * PRODUCT_DEV-356 でハッシュ一覧用に作ったものを、PRODUCT_DEV-708 で
 * **プロンプト編集画面の唯一の正**へ格上げした。settingId ごとの
 * 「機能・表示名・説明・並び順」はすべてここが持ち、画面側は持たない。
 *
 * ## なぜ 1 か所に集約するのか
 * 以前は表示名が `MeetingSettings.tsx` / `ReportSettings.tsx` / `HashList` 用カタログに
 * 三重定義され、編集導線の一覧（subTabEditorIds）はさらに別だった。その結果
 * PRODUCT_DEV-612 で「enum にはあるが Admin から編集できない」キーが 7 件、
 * 「カタログから漏れてハッシュ一覧にも出ない」キーが 1 件、放置されていた。
 *
 * ## 網羅性の担保
 * `FEATURE_MEMBERS` は `as const satisfies` + `AssertNever` で **全 settingId が
 * ちょうど 1 つの機能に属すること**を型で強制する。enum にキーを足してここに
 * 載せ忘れるとコンパイルエラーになる。
 */
import type { ONPPromptSettingId } from "@shared-domain/admin/contract";
import {
  ONPPromptSettingIdSchema,
  ALL_REPORT_PROMPT_SETTING_IDS,
  REPORT_PROMPT_TYPES,
  getReportPromptSettingId,
  type ReportPromptType,
} from "@shared-domain/admin/contract";
import { REPORT_CATEGORIES } from "@shared-domain/unizo/contract";

/**
 * 「機能」= 編集画面の第 1 階層。URL に載るので ASCII のスラッグにする。
 * 分類は PRODUCT_DEV-708 以前のサブタブ構成をそのまま引き継いでいる。
 */
export type PromptFeature =
  | "recording"
  | "summary"
  | "assist"
  | "gatekeeper"
  | "minutes"
  | "review"
  | "search"
  | "report"
  | "base";

export interface PromptFeatureMeta {
  id: PromptFeature;
  label: string;
  /** 機能ボタンを選んだとき、カード未選択の右ペインに出す説明 */
  description: string;
}

/**
 * 機能ボタンの表示順。
 *
 * 土台（ONP基盤）を左端に置き、そこから商談記録の一連の流れ
 * （録音 → 録音後の抽出/補完 → 要約 → 入力補助）、次に議事録まわり
 * （議事録 → 振り返り）、最後に検索とレポートという並び。
 *
 * この配列の順序は**カタログの並び順そのもの**でもあるため、
 * ハッシュ一覧の行順もこれに追従する。
 */
export const PROMPT_FEATURES: PromptFeatureMeta[] = [
  {
    id: "base",
    label: "ONP基盤",
    description:
      "全プロンプトの土台になる Layer 1 / Layer 2。ここを変えると全機能に影響するため、変更は慎重に。",
  },
  {
    id: "recording",
    label: "商談記録",
    description:
      "録音中の AI の振る舞いを決めるプロンプト群。プリセットを持たないユーザのベースルール、法人／個人の検索ルール、コピーモードの指示、対話キャラクターのトーンなど。",
  },
  {
    id: "gatekeeper",
    label: "GateKeeper（補完）",
    description:
      "録音後に走る抽出・チェック・補完のプロンプト群。項目抽出 → グローバルチェック → ヒアリングメモ抽出の順に実行される。",
  },
  {
    id: "summary",
    label: "要約",
    description:
      "商談グループの要約と、日報／週報／月報の活動サマリを生成するプロンプト群。録音そのものには関与しない。",
  },
  {
    id: "assist",
    label: "入力補助",
    description:
      "自由記録モードのガイド生成、プリセットの AI 作成、CRM コピー文の要約、法人名カナ推定など、入力を助けるプロンプト群。",
  },
  {
    id: "minutes",
    label: "議事録",
    description:
      "議事録の文字起こしとその構造化（要約・決定事項・Next アクション）に使うプロンプト群。",
  },
  {
    id: "review",
    label: "振り返り",
    description:
      "「商談の振り返りを記録」で始まる録音のプロンプト群。対象の商談のプリセット（項目と、プリセットごとの追加観点）を土台に、ここの文を重ねて対話と抽出が決まる。全テナント共通。",
  },
  {
    id: "search",
    label: "商談検索",
    description:
      "商談の自然言語検索。クエリ解釈 → リランク → 回答生成の 3 段で動く。",
  },
  {
    id: "report",
    label: "顧客レポート",
    description:
      "Unizo 顧客レポートのカテゴリ別プロンプト。8 カテゴリ × 3 種別（サマリ生成／口コミ抽出／対話入力）で 24 本。",
  },
];

/** PRODUCT_DEV-1142 ③b: まとめ方ごとの観点（3つ）に共通の説明 */
const TEAM_SUMMARY_FOCUS_DESCRIPTION =
  "部のまとめプロンプト（TEAM_SUMMARY_BODY）の**末尾に足して使う**、まとめ方ごとの観点（PRODUCT_DEV-1142）。まとめ方は部署ごとに、その部署のマネージャーが「フォーマットと配信」で選ぶ（部署ごとの管理を使う会社だけ。選んでいない部署・使わない会社はフォロー重視）。材料と JSON の形はどの型も同じで、ここで変えるのは厚く書くところ（どの欄をどれだけ書くか・件数の上限の読み替え）だけ。**JSON の形や項目名（urgent / progress / members / companies / attention）の指示はここに書かない**（部のまとめプロンプトの側にあり、両方に書くと食い違う）。並べる順（画面・メール・読み上げ）はプログラムが決めるので、ここで順番を指示しても変わらない。部のまとめプロンプトの上書きが旧文（JSON の語が無い）のときは、この観点は足されない。";

/**
 * 既定の機能（`/onp-settings` の着地先・不正スラッグの寄せ先・サブナビのリンク先）。
 *
 * **必ず先頭の機能から導出する。** 直書きすると並び替えたときに
 * 「左端のボタンと着地先が食い違う」壊れ方をする。
 */
export const DEFAULT_PROMPT_FEATURE: PromptFeature =
  PROMPT_FEATURES[0]?.id ?? "recording";

/**
 * 機能 → その機能に属する settingId（**画面の並び順そのまま**）。
 *
 * 顧客レポート（report）は 8 カテゴリ × 3 種別から自動生成するためここには含めない。
 */
const FEATURE_MEMBERS = {
  recording: [
    "PRODUCT_BASE",
    "COMPANY_SEARCH",
    "INDIVIDUAL_COMPANY_SEARCH",
    "DICTIONARY_TEMPLATE",
    "GROUP_CONTEXT",
    "COPY_MODE_COMPANY",
    "COPY_MODE_INDIVIDUAL",
    "CHARACTER_TONE",
  ],
  summary: [
    "GROUP_SUMMARY_GENERATION",
    "GROUP_SUMMARY_GENERATION_AI",
    "ACTIVITY_SUMMARY_BODY",
    "ACTIVITY_SUMMARY_HIGHLIGHT",
    "TEAM_SUMMARY_BODY",
    // PRODUCT_DEV-1142 ③b
    "TEAM_SUMMARY_FOCUS_FOLLOW",
    "TEAM_SUMMARY_FOCUS_RESULT",
    "TEAM_SUMMARY_FOCUS_ACTIVITY",
  ],
  assist: [
    "SPEED_MODE_GUIDE_GENERATION",
    "PRESET_DRAFT_BUILD",
    "PRESET_VOICE_EDIT",
    "MEETING_COPY_SUMMARY",
    "COMPANY_NAME_KANA_SUGGESTION",
  ],
  // 実行順（抽出相 → チェック相 → 第3コール）に並べる。旧2パスは @deprecated だが
  // DB 上書きが残る環境の整理用に末尾へ残置。
  gatekeeper: [
    "MEETING_FIELD_EXTRACTION",
    "MEETING_FIELD_EXTRACTION_BULLET_STYLE",
    "MEETING_GLOBAL_CHECK",
    "HEARING_NOTES_EXTRACTION",
    "TRANSCRIPT_CLEAN",
    "INDIVIDUAL_PROFILE_EXTRACTION",
    "CONTACT_SIMILARITY_MATCH",
    "MEETING_COMPLETION",
    "MEETING_ITEM_CHECK",
  ],
  minutes: ["MINUTES_STRUCTURE", "MINUTES_TRANSCRIBE", "MINUTES_CLEAN"],
  // 対話（本体・観点・キーマン・追記・ツール実行・聞き足し）→ 議事録の埋め込み → 録音後の抽出
  review: [
    "MINUTES_REVIEW_PROMPT",
    "MINUTES_REVIEW_PERSPECTIVES",
    "MINUTES_REVIEW_KEYMAN_GUIDE",
    "MINUTES_REVIEW_APPEND",
    "MINUTES_REVIEW_TOOL_RULE",
    "MINUTES_REVIEW_FOLLOWUP",
    "MINUTES_REVIEW_CONTEXT",
    "MINUTES_REVIEW_EXTRACTION",
  ],
  // 解釈 → リランク → 回答生成の実行順
  search: ["MEETING_NL_SEARCH", "MEETING_NL_RERANK", "MEETING_NL_ANSWER"],
  base: ["LAYER1", "LAYER2"],
} as const satisfies Record<
  Exclude<PromptFeature, "report">,
  readonly ONPPromptSettingId[]
>;

/**
 * **全 settingId がいずれかの機能に載っていること**を型で保証する。
 * 未配置のキーがあると `UnplacedSettingId` が `never` にならず、下の型でコンパイルエラーになる。
 */
type PlacedSettingId =
  (typeof FEATURE_MEMBERS)[Exclude<PromptFeature, "report">][number];
type UnplacedSettingId = Exclude<ONPPromptSettingId, PlacedSettingId>;
type AssertNever<T extends never> = T;
export type PromptFeatureCoverage = AssertNever<UnplacedSettingId>;

/**
 * settingId → 機能（`FEATURE_MEMBERS` から導出。二重管理しない）。
 *
 * 上の `AssertNever` で全キーの網羅が保証されているので、
 * `Record<ONPPromptSettingId, ...>`（= 引き当てが必ず成功する型）として扱ってよい。
 */
const SETTING_FEATURE = Object.fromEntries(
  Object.entries(FEATURE_MEMBERS).flatMap(([feature, ids]) =>
    (ids as readonly string[]).map((id) => [id, feature as PromptFeature]),
  ),
) as Record<ONPPromptSettingId, PromptFeature>;

export interface PromptCatalogEntry {
  settingId: string;
  title: string;
  /** カード一覧と編集ペインに出す説明。何に効くのか・プレースホルダ・注意点を書く */
  description: string;
  feature: PromptFeature;
  /** 顧客レポートのみ: カテゴリ名（カード上のバッジ） */
  categoryLabel?: string;
  /** ハッシュ一覧の粗いグループ分け（機能から導出） */
  group: PromptCatalogGroup;
  /** 該当プロンプトを開く編集画面のパス */
  editPath: string;
}

export type PromptCatalogGroup = "ONP基盤" | "商談記録" | "顧客レポート";

function groupOf(feature: PromptFeature): PromptCatalogGroup {
  if (feature === "base") return "ONP基盤";
  if (feature === "report") return "顧客レポート";
  return "商談記録";
}

export function promptEditPath(
  feature: PromptFeature,
  settingId: string,
): string {
  return `/onp-settings/${feature}?id=${settingId}`;
}

/**
 * settingId → 表示名と説明。
 *
 * `Record<..., ...>` で全キー必須。説明は「何に効くか」「どの条件で注入されるか」
 * 「置換されるプレースホルダ」「書くときの注意」を書く。運用者はここしか読まない。
 */
const SETTING_LABELS: Record<
  ONPPromptSettingId,
  { title: string; description: string }
> = {
  LAYER1: {
    title: "Layer 1 (ONP基本ルール)",
    description:
      "音声 AI の基本的な振る舞いルール。全録音プロンプトの土台になるため、変更は全機能に波及する。",
  },
  LAYER2: {
    title: "Layer 2 (対話形式ルール)",
    description:
      "対話フローの制御ルール。Layer 1 の直後に置かれ、こちらも全録音プロンプトに効く。",
  },
  PRODUCT_BASE: {
    title: "商談記録ベースルール (Layer 3a)",
    description:
      "プリセットを持たないユーザ（既定プリセット）の録音プロンプト本体。ツール実行ルール・取得項目・初回動作を含む。**上書きは全文置換**で、プリセットを持つユーザには注入されない。恒久的な変更はソースへ入れて上書きは削除すること。",
  },
  COMPANY_SEARCH: {
    title: "法人検索ルール",
    description: "法人名の正規化・検索ルール。",
  },
  INDIVIDUAL_COMPANY_SEARCH: {
    title: "個人検索ルール（法人検索ルールの個人版）",
    description:
      "プリセットの情報種別が「個人」かつ個人検索が有効なときに、法人検索ルールの代わりに注入される。録音中に個人マスタを氏名照合し、登録済みの属性（年齢・収入等）を聞き直さないための指示。",
  },
  DICTIONARY_TEMPLATE: {
    title: "辞書テンプレート",
    description: "{entries} がエントリで置換される。",
  },
  GROUP_CONTEXT: {
    title: "続き記録 埋め込みプロンプト",
    description:
      "グループコンテキスト（過去商談要約）を録音プロンプトに注入する際のテンプレート。",
  },
  COPY_MODE_COMPANY: {
    title: "コピーモード指示（法人）",
    description:
      "法人商談をコピーして記録するときの指示部。データ部（✅法人名/✅担当者の実値）はコードが自動生成し、この本文がその下に入る。条件ブロック [[CONTACTS_FIXED]]（担当者あり・追加なし）/ [[CONTACTS_NEW]]（担当者あり・追加あり）/ [[NO_CONTACTS]]（担当者なし）で出し分け。プレースホルダ {company} {contactNames} {contactFieldLabel}",
  },
  COPY_MODE_INDIVIDUAL: {
    title: "コピーモード指示（個人）",
    description:
      "個人商談をコピーして記録するときの指示部。引き継いだお客様情報（氏名・年齢・職業等）は聞き直さず、「変わったこと・新しく分かったこと」だけを1回確認して商談内容へ進む。データ部はコードが自動生成。プレースホルダ {customerInfo}",
  },
  CHARACTER_TONE: {
    title: "対話キャラクター トーン指示",
    description:
      "全テナントの録音プロンプトで L2 の直後に必ず注入される（PRODUCT_DEV-716 でフラグ撤去）。Realtime API に声の高さを変えるパラメータは無いため、話し方の雰囲気はこのプロンプトで制御する。**トーン指示そのものを AI が地の文で喋らないよう明記すること**（PRODUCT_DEV-612 で実際に漏れた）。",
  },
  GROUP_SUMMARY_GENERATION: {
    title: "グループ要約プロンプト（人間用）",
    description: "商談グループの自動要約生成に使用（UI 表示用）。",
  },
  GROUP_SUMMARY_GENERATION_AI: {
    title: "グループ要約プロンプト（AI記録用）",
    description:
      "人間用要約を構造化テキストに変換する（続き記録の AI コンテキスト用）。",
  },
  ACTIVITY_SUMMARY_BODY: {
    title: "活動サマリ 本文プロンプト",
    description:
      "日報/週報/月報の本文下書きを生成する。入力は商談の extractedData とタイトル・顧客名まで（文字起こし全文は渡さない）。集計値はコード側が渡すので数え直させない。評価・順位づけ・煽りを書かせないこと。",
  },
  ACTIVITY_SUMMARY_HIGHLIGHT: {
    title: "活動サマリ ハイライトプロンプト",
    description:
      "顧客ごとの要点を JSON で抽出する。入力に無い顧客名・商談 ID はコード側のガードで落とすため、実在の顧客名をそのまま使うよう指示すること。",
  },
  TEAM_SUMMARY_BODY: {
    title: "部のまとめプロンプト（管理者ビュー）",
    description:
      "部署単位の活動をまとめ、管理者ビューに出す。**1回の生成で3節と要点の一覧を JSON で返させる**（PRODUCT_DEV-698）: `{\"summary\":…,\"members\":…,\"companies\":…,\"attention\":[…]}`。summary は全体タブの先頭に出し、**そのまま Amazon Polly が読み上げる**（メールにも使う）。members はメンバー別タブの「メンバーの動き」、companies は会社別タブの「取引先の動き」。**attention は「目を通したいところ」の AI の判定**（要素ごとに name・level（high/mid/good）・kind・headline・reason・meetingId）で、**JSON の最後に置く**（出力が上限で切れても3節は残る）。meetingId が無い・その商談を name の人が記録していない要素はコード側で捨てる。**kind は high の種類（lost／complaint／negotiation_failed）で、上司宛メールの「要フォロー 高」にはこの種類の呼び方（破談・失注／クレーム／条件交渉が不成立）だけが載る**（headline・reason はメールに載せない）。**上書きするときも出力形式の JSON 指示と \"JSON\" の語、attention の指示を残すこと** — JSON の語が無いと返った全文を summary として扱い見立てが出なくなり、attention の指示が無いと AI の判定だけが黙って止まる（画面は雨・記録なし等のルールの分だけになる）。kind の指示を消すと、上司宛メールには人名だけが載り要点の種類が出なくなる。箇条書き・記号・括弧書き・マークダウンを使わせない。人の評価・順位づけ・煽りも書かせない（件数の少ない人を責める文章にしない）。集計値（部全体・メンバー別の記録/顧客/未記録）はコード側が渡すので数え直させない。summary を長くすると音声が伸び、Polly の 1 リクエスト 3,000 文字を超えると分割合成になる。",
  },
  TEAM_SUMMARY_FOCUS_FOLLOW: {
    title: "部のまとめ まとめ方の観点（フォロー重視）",
    description: `${TEAM_SUMMARY_FOCUS_DESCRIPTION} フォロー重視は、どこに手を打つか（失注・クレーム・条件交渉の不成立・雨）を落とさないための観点。`,
  },
  TEAM_SUMMARY_FOCUS_RESULT: {
    title: "部のまとめ まとめ方の観点（成果重視）",
    description: `${TEAM_SUMMARY_FOCUS_DESCRIPTION} 成果重視は、決まったこと（成約・実行・契約・見送りの決定）を金額と時期つきで厚く書く観点。`,
  },
  TEAM_SUMMARY_FOCUS_ACTIVITY: {
    title: "部のまとめ まとめ方の観点（活動量重視）",
    description: `${TEAM_SUMMARY_FOCUS_DESCRIPTION} 活動量重視は、メンバー全員の件数と次の約束を「メンバー別の集計」の数値どおりに書く観点（評価しない）。`,
  },
  SPEED_MODE_GUIDE_GENERATION: {
    title: "自由記録モード ガイド生成プロンプト",
    description:
      "プリセットのフィールド定義からガイドブロックを自動生成する。{fields} がフィールド定義で置換される。",
  },
  PRESET_DRAFT_BUILD: {
    title: "プリセットAI作成プロンプト",
    description:
      "音声で話した内容からプリセット草案と確認質問を生成する。{fieldCatalog}（共通項目カタログ）/{conversation}（発話ログ）/{previousDraft}/{round}/{maxRounds}/{antipatterns}/{structure} がコード側で置換される。",
  },
  PRESET_VOICE_EDIT: {
    title: "プリセット音声修正プロンプト",
    description:
      "既存プリセットへの「ここをこう直したい」から編集操作（ops）を生成する。プリセット全体は返させないので、言われていない箇所は変わらない。{currentPreset}（今のプリセットの要約。ルール番号 A1/R1/E1 がそのまま操作の index）/{fieldCatalog}（共通項目カタログ）/{instruction}（発話）がコード側で置換される。記録の仕方を台本へ入れない・終了の合図を消させないの担保はコード側にあるので本文で説得しないこと。",
  },
  MEETING_COPY_SUMMARY: {
    title: "CRMコピー文 文字数上限要約プロンプト",
    description:
      "商談詳細「コピーする」のテキストがプリセットの文字数上限（例: 連携先 CRM の上限が 700 文字）を超えたとき、上限以内に要約する。{maxLength} が目標文字数で置換される。URL 行は一字一句保持・原文にない情報の創作は禁止。",
  },
  COMPANY_NAME_KANA_SUGGESTION: {
    title: "法人名カナ候補生成プロンプト",
    description:
      "gBizInfo 側にヨミガナが無い法人について、法人名からカタカナの読み仮名を推定する。「株式会社」等の法人種別は読み仮名に含めない。出力は読み仮名のみ。",
  },
  MEETING_FIELD_EXTRACTION: {
    title: "項目抽出（GateKeeper 抽出相）プロンプト",
    description:
      "対話ログからフィールド（サブフィールド含む）単位で焦点抽出する system プロンプト（gpt-5.4-mini・グループ並列）。{dictionarySection} のみ動的置換。対象項目/既存データ/守備範囲外の他項目は実行時に対話ログの後ろへ別メッセージで付与される。",
  },
  MEETING_FIELD_EXTRACTION_BULLET_STYLE: {
    title: "議事録からの項目抽出（GateKeeper 抽出相）プロンプト",
    description:
      "議事録から商談記録を作るときだけ、項目抽出プロンプト（MEETING_FIELD_EXTRACTION）の末尾に連結される箇条書きの書式ルール。通常の録音から作る商談記録には使われない。動的置換は無い。GateKeeper Playground の議事録URLモードで、保存する前に試せる。",
  },
  MEETING_GLOBAL_CHECK: {
    title: "グローバルチェック（GateKeeper チェック相）プロンプト",
    description:
      "抽出済みの全項目を対話ログと突き合わせ、null 救済・配置是正・消失防止（備考 OK/消失 NG）・過剰重複整理を行う system プロンプト（gpt-5.4・常時1コール）。{fieldList}/{currentData}/{dictionarySection} が動的置換される。",
  },
  HEARING_NOTES_EXTRACTION: {
    title: "ヒアリングメモ抽出（GateKeeper 第3コール）プロンプト",
    description:
      "チェック相の後に1回実行し、「◯◯さんのお話では」のように明示的に特定人物へ帰属された発言のうち既存の記録項目に当てはまらないものを抽出する（gpt-5.4-mini）。個人商談は「その他ヒアリングできた事項」へ、法人商談は企業担当者のメモ欄へ格納。全テナントで実行（PRODUCT_DEV-716 でフラグ撤去）。{dictionarySection} のみ動的置換。",
  },
  TRANSCRIPT_CLEAN: {
    title: "音声ログ整形プロンプト（フィラー・ハルシネーション除去）",
    description:
      "対話ログのユーザ発話を整形する。フィラーワード除去・辞書正規化に加え、動画・字幕由来の定型ハルシネーション（「ご視聴ありがとうございました」等）を除去する。{dictionarySection} が動的値で置換される。",
  },
  INDIVIDUAL_PROFILE_EXTRACTION: {
    title: "個人プロファイル抽出プロンプト（顧客マスタ登録）",
    description:
      "プリセットに customer_info を含む「個人向け商談」の完了時に、対話ログと抽出済みデータから顧客マスタ「個人」の静的カラム（職業・特徴・年齢・収入・家族構成）を抽出する。{conversationLog} / {extractedData} が動的置換される。as-of 基準日はサーバ側（完了日）が付与するため、日付は出力させないこと。",
  },
  CONTACT_SIMILARITY_MATCH: {
    title: "担当者 類似判定プロンプト",
    description:
      "商談の本登録（reviewed=true 確定）直前に、編集後の担当者リストと顧客マスタの既存担当者リストを突き合わせ、同一人物候補を JSON で返す。候補があるときだけフロントが統合モーダルを出す。similarity は high/medium のみ返し、low は出さないこと。",
  },
  MEETING_COMPLETION: {
    title: "【旧】商談データ補完（GateKeeper Pass1）プロンプト",
    description:
      "【@deprecated PRODUCT_DEV-589】旧2パス方式の補完プロンプト。新フローでは未使用（MEETING_FIELD_EXTRACTION へ移行）。DB 上書きが残る環境の整理用。{fieldList}/{normalizedData}/{dictionarySection}",
  },
  MEETING_ITEM_CHECK: {
    title: "【旧】項目チェック（GateKeeper Pass2）プロンプト",
    description:
      "【@deprecated PRODUCT_DEV-589】旧2パス方式の項目チェック。新フローでは未使用（MEETING_GLOBAL_CHECK へ移行）。{fieldList}/{expandedData}/{dictionarySection}",
  },
  MINUTES_STRUCTURE: {
    title: "議事録 構造化プロンプト（要約/決定事項/Nextアクション）",
    description:
      "議事録の文字起こし全文を分析し、タイトル・AI 要約・決定事項・Next アクションを抽出する。finalize worker が全文へ1回だけ実行する。",
  },
  MINUTES_TRANSCRIBE: {
    title: "議事録 チャンク文字起こしプロンプト（文脈/語彙ヒント）",
    description:
      "議事録の音声チャンクを文字起こしする際の文脈・語彙ヒント。chunk worker が基底プロンプトに使用し、前チャンク末尾を連結する。無音・雑音は推測で補わない。",
  },
  MINUTES_CLEAN: {
    title: "議事録 文字起こしクリーニングプロンプト（Gatekeeper）",
    description:
      "議事録の生文字起こしをフィラー・言い直し・無音由来のハルシネーション除去できれいにする。意味内容は変えず推測を混ぜない。{dictionarySection} が動的値で置換される（全テナントで適用）。",
  },
  MINUTES_REVIEW_PROMPT: {
    title: "振り返り 対話プロンプト（ロール・ルール・会話例）",
    description:
      "「商談の振り返りを記録」で始まる録音の本体プロンプト。対象の商談のプリセットの対話プロンプトの代わりに、この本文がまるごと入る（Layer 3a）。議事録に書いてある内容は聞き返させず、その場の相手の反応（声色・表情・目線・仕草・場の空気）だけを1問ずつ聞き出すよう書くこと。締めの言葉「振り返りを終了します」は録音を自動で終える引き金なので消さないこと。プレースホルダなし。",
  },
  MINUTES_REVIEW_PERSPECTIVES: {
    title: "振り返り 共通の観点（聞き方・書くこと）",
    description:
      "どの商談でも聞く共通の観点（法人: 盛り上がり・懸念・キーマン・気づき／個人: キーマンの代わりに決め方と時期）の文言。観点ごとに「## id（見出し）」「聞き方: 」「書くこと: 」で書く（改行して続きを書ける）。「聞き方」は AI が質問するときの例、「書くこと」はその観点に何を記録するかの説明で、録音中の記録先の判断と録音後の抽出（GateKeeper）の両方に渡る。id と見出しは変えられない（見出しを書き換えても効かない）。読めない行は既定の文を使う。観点を足す・外すことはできない（会社ごとの観点はプリセットの「振り返りで足す観点」で足す）。",
  },
  MINUTES_REVIEW_KEYMAN_GUIDE: {
    title: "振り返り キーマンの聞き方",
    description:
      "聞く観点にキーマン（最終決裁者）があるとき（法人の商談）だけ、観点の一覧の後ろに付く文。商談記録や議事録に決裁者がもう書かれているときの確かめ方と、書かれていないときの聞き方を書く。プレースホルダなし。",
  },
  MINUTES_REVIEW_APPEND: {
    title: "振り返り 2回目以降（追記モード）の文",
    description:
      "すでに振り返りが記録されている商談で、もう一度振り返りを始めたときだけ入る文。{filledPerspectives}（記録済みの観点と値）と {remainingPerspectives}（まだ空いている観点と聞き方）がコード側で置換される。{filledPerspectives} が無い上書きは使われず、既定の文になる。締めの言葉「振り返りを終了します」は録音を自動で終える引き金なので消さないこと。",
  },
  MINUTES_REVIEW_TOOL_RULE: {
    title: "振り返り ツール実行ルール",
    description:
      "話を聞くたびに記録用のツール（extract_meeting_report）を実行させ、振り返りの欄（minutes_review）の観点へ入れさせるルール。対話プロンプトの直後に必ず入る。ツール名 extract_meeting_report が無い上書きは使われず、既定の文になる（ツールが呼ばれないと記録が何も残らないため）。プレースホルダなし。",
  },
  MINUTES_REVIEW_FOLLOWUP: {
    title: "振り返り 聞き足しの文（記録のたびに送る指示）",
    description:
      "AI が記録用のツールを実行するたびに送る短い指示。毎回・対話プロンプトより後に届くので強く効く（進め方をここに書くと、対話プロンプトの指示を上書きする）。{remainingPerspectives} はまだ聞けていない観点の見出し（「／」区切り）に置換される。[[HAS_REMAINING]]…[[/HAS_REMAINING]] は聞けていない観点があるときだけ、[[ALL_ASKED]]…[[/ALL_ASKED]] はひととおり聞けたときだけ残る。締めの言葉「振り返りを終了します」をそのまま言わせる文は消さないこと（言い換えられると録音が自動で終わらない）。",
  },
  MINUTES_REVIEW_CONTEXT: {
    title: "振り返り 議事録の埋め込みプロンプト",
    description:
      "振り返り対象の議事録を録音プロンプトへ差し込むテンプレート（Layer 3b、「続き記録 埋め込みプロンプト」と同じ位置づけ）。{minutesTitle} {minutesDate} {summary} {decisions} {nextActions} がコード側で置換される（決定事項/Next アクションは箇条書き、空なら「（記載なし）」）。",
  },
  MINUTES_REVIEW_EXTRACTION: {
    title: "振り返り 抽出プロンプト（GateKeeper 項目説明）",
    description:
      "録音後の GateKeeper が「振り返り（相手の反応・様子）」欄へ何を書くかの指示。振り返りの欄の項目説明としてそのまま抽出プロンプトに載る（観点ごとの守備範囲は「共通の観点」の「書くこと」）。会議で決まったこと・話した内容そのものは議事録側にあるため守備範囲外と明記すること。プレースホルダなし。",
  },
  MEETING_NL_SEARCH: {
    title: "商談 自然言語検索 クエリ解釈プロンプト",
    description:
      "自然言語の検索クエリを商談検索フィルタの JSON（MeetingFilterInterpretationSchema 準拠）へ翻訳する。検索パイプラインの第1段。",
  },
  MEETING_NL_RERANK: {
    title: "商談 自然言語検索 リランクプロンプト",
    description:
      "元クエリと候補商談のサマリ（最大50件）を受け取り、関連度順に並べた商談 ID 配列を返す。検索パイプラインの第2段。",
  },
  MEETING_NL_ANSWER: {
    title: "商談 自然言語検索 回答生成プロンプト",
    description:
      "検索結果を根拠に回答文を生成する（第3段）。PRODUCT_DEV-716 で全テナントが議事録/メモ/ToDo も引用できる横断版に一本化された（商談のみの従来版は削除）。⚠ 横断化以前の文言で上書きしたままだと横断引用が出ないため、上書きを置いている場合は本文を横断版へ更新すること。",
  },
};

/** 顧客レポート種別ラベル */
const REPORT_TYPE_LABELS: Record<
  ReportPromptType,
  { title: string; description: string }
> = {
  SUMMARY: {
    title: "サマリ生成",
    description:
      "全商談＋エントリーからこのカテゴリのインサイトを生成するプロンプト。",
  },
  EXTRACT: {
    title: "口コミ抽出",
    description:
      "1 商談のトランスクリプトからこのカテゴリのエントリーを切り出すプロンプト。",
  },
  DIALOGUE: {
    title: "対話入力",
    description:
      "音声入力時に AI がこのカテゴリについて質問するプロンプト（短く）。",
  },
};

function onpEntry(settingId: ONPPromptSettingId): PromptCatalogEntry {
  const feature = SETTING_FEATURE[settingId];
  const label = SETTING_LABELS[settingId];
  return {
    settingId,
    title: label.title,
    description: label.description,
    feature,
    group: groupOf(feature),
    editPath: promptEditPath(feature, settingId),
  };
}

/** ONP 系 35 件（機能ごとの並び順のまま連結） */
const ONP_ENTRIES: PromptCatalogEntry[] = PROMPT_FEATURES.filter(
  (f) => f.id !== "report",
).flatMap((f) =>
  (FEATURE_MEMBERS[f.id as Exclude<PromptFeature, "report">] as readonly ONPPromptSettingId[]).map(
    onpEntry,
  ),
);

/** 顧客レポート 24 件（8 カテゴリ × 3 種別） */
const REPORT_ENTRIES: PromptCatalogEntry[] = REPORT_CATEGORIES.flatMap((cat) =>
  REPORT_PROMPT_TYPES.map((type) => ({
    settingId: getReportPromptSettingId(type, cat.id),
    title: `${cat.title} - ${REPORT_TYPE_LABELS[type].title}`,
    description: REPORT_TYPE_LABELS[type].description,
    feature: "report" as const,
    categoryLabel: cat.title,
    group: "顧客レポート" as const,
    editPath: promptEditPath("report", getReportPromptSettingId(type, cat.id)),
  })),
);

const BASE_CATALOG: PromptCatalogEntry[] = [...ONP_ENTRIES, ...REPORT_ENTRIES];

/**
 * 保険: contract に存在するが BASE_CATALOG に載っていない settingId を末尾に補完する。
 * `FEATURE_MEMBERS` の型ガードで ONP 側は落ちない想定だが、レポート側の id 生成規則が
 * 変わったときに一覧から黙って消えるのを防ぐ。
 */
function buildCatalog(): PromptCatalogEntry[] {
  const known = new Set(BASE_CATALOG.map((e) => e.settingId));
  const extra: PromptCatalogEntry[] = [];

  for (const id of ONPPromptSettingIdSchema.options) {
    if (!known.has(id)) {
      extra.push({
        settingId: id,
        title: id,
        description: "（カタログ未登録）",
        feature: "recording",
        group: "商談記録",
        editPath: promptEditPath("recording", id),
      });
      known.add(id);
    }
  }
  for (const id of ALL_REPORT_PROMPT_SETTING_IDS) {
    if (!known.has(id)) {
      extra.push({
        settingId: id,
        title: id,
        description: "（カタログ未登録）",
        feature: "report",
        group: "顧客レポート",
        editPath: promptEditPath("report", id),
      });
      known.add(id);
    }
  }

  return [...BASE_CATALOG, ...extra];
}

/** 全プロンプトの固定順カタログ */
export const PROMPT_CATALOG: PromptCatalogEntry[] = buildCatalog();

/** 機能 → その機能のカタログ行（表示順） */
export function catalogByFeature(feature: PromptFeature): PromptCatalogEntry[] {
  return PROMPT_CATALOG.filter((e) => e.feature === feature);
}

/** settingId → カタログ行 */
export function catalogEntryOf(
  settingId: string,
): PromptCatalogEntry | undefined {
  return PROMPT_CATALOG.find((e) => e.settingId === settingId);
}

/** 表示順を保ったグループ一覧（ハッシュ一覧で使用） */
export const PROMPT_CATALOG_GROUPS: PromptCatalogGroup[] = [
  "ONP基盤",
  "商談記録",
  "顧客レポート",
];
